/**
 * Base interface for row failures used in upload results.
 * Each uploader extends this with their own identifier field.
 */
interface BaseRowFailure {
  rowNumber: number;
  errors: string[];
}

/**
 * Formats failed rows into a plain text string suitable for copying to clipboard.
 * Takes a generic row failure type and extracts the identifier for display.
 *
 * @param failedRows - Array of row failures to format
 * @param getIdentifierSuffix - Function that extracts an optional suffix for the row header
 *   (e.g., returns '(serial "ABC123")' or undefined if no identifier available)
 * @returns Formatted string with each error on its own line
 */
export function formatFailedRowsForCopy<T extends BaseRowFailure>(
  failedRows: T[],
  getIdentifierSuffix: (row: T) => string | undefined
): string {
  return failedRows
    .map((row) => {
      const suffix = getIdentifierSuffix(row);
      const header = suffix
        ? `Row ${String(row.rowNumber)} ${suffix}`
        : `Row ${String(row.rowNumber)}`;
      const errors = row.errors
        .map((error) => `${header}: ${error}`)
        .join('\n');
      return errors;
    })
    .join('\n');
}

/**
 * Helper to create a simple identifier suffix for single-field identifiers.
 * Use with formatFailedRowsForCopy for the common case of a single identifier field.
 *
 * @param row - The row failure object
 * @param field - The field name to extract the identifier from
 * @param label - The label to use in the suffix (e.g., 'serial', 'product')
 * @returns Formatted suffix string or undefined if the field is empty
 */
export function createIdentifierSuffix<T>(
  row: T,
  field: keyof T,
  label: string
): string | undefined {
  const value = row[field];
  return value !== null && value !== undefined
    ? `(${label} "${String(value)}")`
    : undefined;
}
